import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  FileCode, 
  Upload, 
  Blocks, 
  User, 
  LogOut,
  Activity,
  Zap,
  Package
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeveloperDashboard = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [stats, setStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const username = localStorage.getItem('dev_username');
  const token = localStorage.getItem('dev_token');

  useEffect(() => {
    if (!token) {
      navigate('/developer/login');
      return;
    }
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, contractsRes] = await Promise.all([
        axios.get(`${API}/developer/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/contracts/my`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(statsRes.data);
      setContracts(contractsRes.data.contracts || []);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/developer/login');
      }
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Logged out successfully');
    navigate('/developer/login');
  };

  const StatCard = ({ icon: Icon, title, value, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass p-6 rounded-sm"
    >
      <div className="flex items-center gap-4">
        <div className={`p-3 bg-${color}/10 rounded-sm`}>
          <Icon size={28} className={`text-${color}`} strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-text-muted text-sm">{title}</p>
          <h3 className="font-unbounded text-3xl font-bold text-text-primary">
            {value || '0'}
          </h3>
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-screen w-64 bg-background-card border-r border-border p-6">
        <div className="mb-8">
          <h1 className="font-unbounded text-xl font-bold text-primary mb-1">
            Developer Portal
          </h1>
          <p className="text-text-muted text-sm">Smart Contracts</p>
        </div>

        <nav className="space-y-2">
          <button
            onClick={() => setActiveTab('home')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'home'
                ? 'bg-primary/10 text-primary border border-primary/30'
                : 'text-text-secondary hover:bg-background-subtle'
            }`}
            data-testid="tab-home"
          >
            <LayoutDashboard size={20} />
            <span>Home</span>
          </button>
          
          <button
            onClick={() => setActiveTab('contracts')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'contracts'
                ? 'bg-primary/10 text-primary border border-primary/30'
                : 'text-text-secondary hover:bg-background-subtle'
            }`}
            data-testid="tab-contracts"
          >
            <FileCode size={20} />
            <span>My Contracts</span>
          </button>
          
          <button
            onClick={() => setActiveTab('deploy')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'deploy'
                ? 'bg-primary/10 text-primary border border-primary/30'
                : 'text-text-secondary hover:bg-background-subtle'
            }`}
            data-testid="tab-deploy"
          >
            <Upload size={20} />
            <span>Deploy Contract</span>
          </button>
          
          <button
            onClick={() => setActiveTab('explorer')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'explorer'
                ? 'bg-primary/10 text-primary border border-primary/30'
                : 'text-text-secondary hover:bg-background-subtle'
            }`}
            data-testid="tab-explorer"
          >
            <Blocks size={20} />
            <span>Explorer</span>
          </button>
          
          <button
            onClick={() => setActiveTab('profile')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'profile'
                ? 'bg-primary/10 text-primary border border-primary/30'
                : 'text-text-secondary hover:bg-background-subtle'
            }`}
            data-testid="tab-profile"
          >
            <User size={20} />
            <span>Profile</span>
          </button>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="glass p-4 rounded-sm mb-4">
            <p className="text-xs text-text-muted mb-1">Logged in as</p>
            <p className="font-bold text-primary">{username}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent-error text-white rounded-sm hover:opacity-90 transition-opacity"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        {activeTab === 'home' && (
          <div>
            <h2 className="font-unbounded text-3xl font-bold text-text-primary mb-6">
              Dashboard Overview
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <StatCard
                icon={FileCode}
                title="Contracts Deployed"
                value={stats?.contracts_deployed}
                color="primary"
              />
              <StatCard
                icon={Zap}
                title="Total Gas Used"
                value={stats?.total_gas_used?.toLocaleString()}
                color="accent-warning"
              />
              <StatCard
                icon={Activity}
                title="Active Contracts"
                value={contracts.length}
                color="accent-success"
              />
            </div>

            <div className="glass p-6 rounded-sm">
              <h3 className="font-unbounded text-xl font-bold text-text-primary mb-4">
                Recent Contracts
              </h3>
              {contracts.length > 0 ? (
                <div className="space-y-3">
                  {contracts.slice(0, 5).map((contract) => (
                    <div
                      key={contract.contract_id}
                      className="p-4 bg-background-subtle rounded-sm border border-border"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-bold text-text-primary">{contract.name}</h4>
                          <p className="text-sm text-text-muted font-mono">
                            {contract.contract_id}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-text-muted">Executions</p>
                          <p className="font-bold text-primary">{contract.execution_count || 0}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Package size={48} className="mx-auto text-text-muted mb-4" />
                  <p className="text-text-muted">No contracts deployed yet</p>
                  <button
                    onClick={() => setActiveTab('deploy')}
                    className="mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-sm hover:bg-primary-hover transition-colors"
                  >
                    Deploy Your First Contract
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'contracts' && (
          <div>
            <h2 className="font-unbounded text-3xl font-bold text-text-primary mb-6">
              My Contracts
            </h2>
            {contracts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {contracts.map((contract) => (
                  <motion.div
                    key={contract.contract_id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass p-6 rounded-sm"
                  >
                    <h3 className="font-unbounded text-xl font-bold text-text-primary mb-2">
                      {contract.name}
                    </h3>
                    <p className="text-sm text-text-muted font-mono mb-4">
                      {contract.contract_id}
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-text-muted">Executions:</span>
                        <span className="text-text-primary font-bold">
                          {contract.execution_count || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-muted">Gas Used:</span>
                        <span className="text-text-primary font-bold">
                          {(contract.total_gas_used || 0).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-muted">Deployed:</span>
                        <span className="text-text-primary">
                          {new Date(contract.deployed_at * 1000).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="glass p-12 rounded-sm text-center">
                <FileCode size={64} className="mx-auto text-text-muted mb-4" />
                <p className="text-text-muted mb-4">You haven't deployed any contracts yet</p>
                <button
                  onClick={() => setActiveTab('deploy')}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-sm hover:bg-primary-hover transition-colors"
                >
                  Deploy Your First Contract
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'deploy' && (
          <div>
            <h2 className="font-unbounded text-3xl font-bold text-text-primary mb-6">
              Deploy Smart Contract
            </h2>
            <div className="glass p-8 rounded-sm max-w-2xl">
              <p className="text-text-secondary mb-6">
                Upload your compiled WebAssembly (.wasm) file to deploy it to the blockchain.
              </p>
              {/* Deploy form would go here - simplified for package */}
              <div className="space-y-4">
                <div>
                  <label className="text-text-secondary text-sm mb-2 block">Contract Name</label>
                  <input
                    type="text"
                    className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none"
                    placeholder="My Smart Contract"
                  />
                </div>
                <div>
                  <label className="text-text-secondary text-sm mb-2 block">WASM File</label>
                  <input
                    type="file"
                    accept=".wasm"
                    className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary"
                  />
                </div>
                <div>
                  <label className="text-text-secondary text-sm mb-2 block">ABI (JSON)</label>
                  <textarea
                    rows="6"
                    className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none font-mono text-sm"
                    placeholder='{"functions":[{"name":"increment","params":[],"returns":["i32"]}]}'
                  />
                </div>
                <button
                  className="w-full py-3 bg-secondary text-secondary-foreground font-bold rounded-sm hover:opacity-90 transition-opacity"
                >
                  Deploy Contract
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'explorer' && (
          <div>
            <h2 className="font-unbounded text-3xl font-bold text-text-primary mb-6">
              Contract Explorer
            </h2>
            <div className="glass p-6 rounded-sm">
              <p className="text-text-secondary">Browse all deployed contracts on the blockchain</p>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div>
            <h2 className="font-unbounded text-3xl font-bold text-text-primary mb-6">
              Developer Profile
            </h2>
            <div className="glass p-8 rounded-sm max-w-2xl">
              <div className="space-y-6">
                <div>
                  <label className="text-text-muted text-sm mb-2 block">Username</label>
                  <p className="font-bold text-text-primary text-xl">{username}</p>
                </div>
                <div>
                  <label className="text-text-muted text-sm mb-2 block">API Key</label>
                  <div className="flex items-center gap-2">
                    <p className="flex-1 p-3 bg-background-subtle border border-border rounded-sm font-mono text-sm text-text-primary">
                      {stats?.api_key || localStorage.getItem('dev_api_key')}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-text-muted text-sm mb-2 block">Contracts</label>
                    <p className="font-bold text-primary text-2xl">
                      {stats?.contracts_deployed || 0}
                    </p>
                  </div>
                  <div>
                    <label className="text-text-muted text-sm mb-2 block">Total Gas</label>
                    <p className="font-bold text-accent-warning text-2xl">
                      {(stats?.total_gas_used || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeveloperDashboard;
